import React, { useState, useEffect, useCallback } from "react";
import { useTenantNavigation } from "@/hooks/useTenantNavigation";
import { useAuth } from "@/contexts/AuthContext";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { LogOut, FileText, Download, Calendar, Filter, Clock, Truck, Mountain, DollarSign, TrendingUp, Loader2 } from "lucide-react";
import { Logo } from "@/components/Logo";
import { NotificationBell } from "@/components/NotificationBell";

const API_BASE = process.env.REACT_APP_BACKEND_URL;

function getPresetDates(preset) {
  const today = new Date();
  const fmt = (d) => d.toISOString().slice(0, 10);
  switch (preset) {
    case "this_week": {
      const d = new Date(today); d.setDate(d.getDate() - d.getDay());
      return { from: fmt(d), to: fmt(today) };
    }
    case "last_week": {
      const d = new Date(today); d.setDate(d.getDate() - d.getDay() - 7);
      const e = new Date(d); e.setDate(e.getDate() + 6);
      return { from: fmt(d), to: fmt(e) };
    }
    case "this_month": {
      return { from: fmt(new Date(today.getFullYear(), today.getMonth(), 1)), to: fmt(today) };
    }
    case "last_month": {
      const s = new Date(today.getFullYear(), today.getMonth() - 1, 1);
      const e = new Date(today.getFullYear(), today.getMonth(), 0);
      return { from: fmt(s), to: fmt(e) };
    }
    case "this_year": {
      return { from: fmt(new Date(today.getFullYear(), 0, 1)), to: fmt(today) };
    }
    default: return null;
  }
}

export default function ConsolidatedReportPage() {
  const { user, logout } = useAuth();
  const { navigate } = useTenantNavigation();
  const [lookups, setLookups] = useState({ sites: [], contractors: [] });
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [siteId, setSiteId] = useState("");
  const [contractorId, setContractorId] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState("");

  useEffect(() => {
    api.get("/lookups").then((l) => setLookups(l)).catch(() => {});
    // Default to this month
    const preset = getPresetDates("this_month");
    if (preset) { setDateFrom(preset.from); setDateTo(preset.to); }
  }, []);

  const handlePreset = (val) => {
    const dates = getPresetDates(val);
    if (dates) { setDateFrom(dates.from); setDateTo(dates.to); }
  };

  const generate = useCallback(async () => {
    if (!dateFrom || !dateTo) { toast.error("Select a date range"); return; }
    setLoading(true);
    try {
      const params = new URLSearchParams({ date_from: dateFrom, date_to: dateTo });
      if (siteId) params.append("site_id", siteId);
      if (contractorId) params.append("contractor_id", contractorId);
      const res = await api.get(`/reports/consolidated?${params}`);
      setData(res);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to generate report");
    } finally { setLoading(false); }
  }, [dateFrom, dateTo, siteId, contractorId]);

  const exportFile = async (type) => {
    setExporting(type);
    try {
      const params = new URLSearchParams({ date_from: dateFrom, date_to: dateTo });
      if (siteId) params.append("site_id", siteId);
      if (contractorId) params.append("contractor_id", contractorId);
      const token = localStorage.getItem("token");
      const res = await fetch(`${API_BASE}/api/reports/consolidated/${type}?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error("Export failed");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `consolidated_${dateFrom}_to_${dateTo}.${type}`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(`${type.toUpperCase()} downloaded`);
    } catch { toast.error(`Failed to export ${type.toUpperCase()}`); }
    finally { setExporting(""); }
  };

  const k = data?.kpis || {};

  return (
    <div className="min-h-screen bg-background" data-testid="consolidated-report-page">
      <header className="border-b border-border px-6 py-3 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Logo />
          <span className="text-muted-foreground text-sm">/</span>
          <h1 className="font-headline text-sm font-semibold tracking-wide">Consolidated Report</h1>
        </div>
        <div className="flex items-center gap-3">
          <NotificationBell />
          <Button variant="ghost" size="sm" onClick={() => navigate("/admin")} className="text-xs">Admin</Button>
          <Button variant="ghost" size="icon" onClick={logout} data-testid="logout-button"><LogOut className="h-4 w-4" strokeWidth={1.5} /></Button>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-6">
        {/* Filters */}
        <Card className="border-border bg-card mb-6">
          <CardContent className="py-4 px-5">
            <div className="flex flex-wrap items-end gap-4">
              <div className="space-y-1.5">
                <Label className="text-xs text-muted-foreground">Quick Select</Label>
                <Select onValueChange={handlePreset}>
                  <SelectTrigger className="w-[140px] bg-background border-border text-xs" data-testid="preset-select">
                    <SelectValue placeholder="Presets" />
                  </SelectTrigger>
                  <SelectContent className="bg-card border-border">
                    <SelectItem value="this_week">This Week</SelectItem>
                    <SelectItem value="last_week">Last Week</SelectItem>
                    <SelectItem value="this_month">This Month</SelectItem>
                    <SelectItem value="last_month">Last Month</SelectItem>
                    <SelectItem value="this_year">This Year</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs text-muted-foreground">From</Label>
                <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="w-[150px] bg-background border-border text-sm" data-testid="date-from" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs text-muted-foreground">To</Label>
                <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="w-[150px] bg-background border-border text-sm" data-testid="date-to" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs text-muted-foreground">Site</Label>
                <Select value={siteId || "all"} onValueChange={(v) => setSiteId(v === "all" ? "" : v)}>
                  <SelectTrigger className="w-[160px] bg-background border-border text-xs" data-testid="site-filter"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-card border-border">
                    <SelectItem value="all">All Sites</SelectItem>
                    {lookups.sites?.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs text-muted-foreground">Contractor</Label>
                <Select value={contractorId || "all"} onValueChange={(v) => setContractorId(v === "all" ? "" : v)}>
                  <SelectTrigger className="w-[160px] bg-background border-border text-xs" data-testid="contractor-filter"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-card border-border">
                    <SelectItem value="all">All Contractors</SelectItem>
                    {lookups.contractors?.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <Button onClick={generate} disabled={loading} data-testid="generate-btn" className="font-headline font-semibold">
                {loading ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <FileText className="h-4 w-4 mr-1.5" strokeWidth={1.5} />}
                {loading ? "Generating..." : "Generate"}
              </Button>
            </div>
          </CardContent>
        </Card>

        {data && data.report_count > 0 && (
          <>
            {/* KPI Cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6" data-testid="kpi-cards">
              <KpiCard icon={<Clock className="h-4 w-4" />} label="Operating Hours" value={`${k.total_operating_hours?.toFixed(1)}h`} />
              <KpiCard icon={<Clock className="h-4 w-4" />} label="Delay Hours" value={`${k.total_delay_hours?.toFixed(1)}h`} accent="text-orange-400" />
              <KpiCard icon={<Mountain className="h-4 w-4" />} label="Total Tons" value={`${k.total_tons?.toLocaleString()}`} />
              <KpiCard icon={<Truck className="h-4 w-4" />} label="Total Loads" value={`${k.total_loads?.toLocaleString()}`} />
              <KpiCard icon={<TrendingUp className="h-4 w-4" />} label="Utilization" value={`${k.utilization_pct}%`} accent="text-primary" />
              <KpiCard icon={<Mountain className="h-4 w-4" />} label="Total CYD" value={`${k.total_cyd?.toLocaleString()}`} />
              <KpiCard icon={<FileText className="h-4 w-4" />} label="Reports" value={`${data.report_count}`} />
              <KpiCard icon={<DollarSign className="h-4 w-4" />} label="Equipment Cost" value={`$${k.total_equipment_cost?.toLocaleString()}`} accent="text-primary" />
              <KpiCard icon={<DollarSign className="h-4 w-4" />} label="Material/Hauling Cost" value={`$${(k.total_material_cost || 0).toLocaleString()}`} accent="text-blue-400" />
              <KpiCard icon={<DollarSign className="h-4 w-4" />} label="Total Cost" value={`$${(k.total_cost || k.total_equipment_cost || 0).toLocaleString()}`} accent="text-amber-400" />
            </div>

            {/* Equipment Breakdown */}
            <div className="mb-6">
              <h2 className="font-headline text-sm font-semibold tracking-wide uppercase text-muted-foreground mb-3">Equipment Breakdown & Costs</h2>
              <Card className="border-border bg-card overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow className="border-border hover:bg-transparent">
                      <TableHead className="text-muted-foreground text-xs uppercase">Unit</TableHead>
                      <TableHead className="text-muted-foreground text-xs uppercase">Type</TableHead>
                      <TableHead className="text-muted-foreground text-xs uppercase text-right">Op Hours</TableHead>
                      <TableHead className="text-muted-foreground text-xs uppercase text-right">Delay</TableHead>
                      <TableHead className="text-muted-foreground text-xs uppercase text-right">Utilization</TableHead>
                      <TableHead className="text-muted-foreground text-xs uppercase text-right">Rate ($/h)</TableHead>
                      <TableHead className="text-muted-foreground text-xs uppercase text-right">Cost</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {data.equipment_breakdown.map((eq) => (
                      <TableRow key={eq.id} className="border-border" data-testid={`equip-row-${eq.id}`}>
                        <TableCell className="text-sm font-medium">{eq.unit_id}</TableCell>
                        <TableCell className="text-sm text-muted-foreground">{eq.type}</TableCell>
                        <TableCell className="text-sm text-right font-mono">{eq.op_hours.toFixed(1)}</TableCell>
                        <TableCell className="text-sm text-right font-mono text-orange-400">{eq.delay_hours.toFixed(1)}</TableCell>
                        <TableCell className="text-sm text-right">
                          <Badge variant="outline" className={`text-xs ${eq.utilization >= 80 ? "bg-primary/20 text-primary border-primary/30" : "bg-muted text-muted-foreground border-border"}`}>{eq.utilization}%</Badge>
                        </TableCell>
                        <TableCell className="text-sm text-right font-mono">${eq.hourly_rate.toFixed(2)}</TableCell>
                        <TableCell className="text-sm text-right font-mono font-semibold">${eq.cost.toLocaleString(undefined, { minimumFractionDigits: 2 })}</TableCell>
                      </TableRow>
                    ))}
                    <TableRow className="border-border bg-primary/5">
                      <TableCell className="text-sm font-bold">TOTAL</TableCell>
                      <TableCell />
                      <TableCell className="text-sm text-right font-mono font-bold">{k.total_operating_hours?.toFixed(1)}</TableCell>
                      <TableCell className="text-sm text-right font-mono font-bold text-orange-400">{k.total_delay_hours?.toFixed(1)}</TableCell>
                      <TableCell className="text-sm text-right"><Badge variant="outline" className="text-xs bg-primary/20 text-primary border-primary/30">{k.utilization_pct}%</Badge></TableCell>
                      <TableCell />
                      <TableCell className="text-sm text-right font-mono font-bold text-primary">${k.total_equipment_cost?.toLocaleString(undefined, { minimumFractionDigits: 2 })}</TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </Card>
            </div>

            {/* Material Breakdown */}
            {data.material_breakdown?.length > 0 && (
              <div className="mb-6">
                <h2 className="font-headline text-sm font-semibold tracking-wide uppercase text-muted-foreground mb-3">Material Movements</h2>
                <Card className="border-border bg-card overflow-hidden">
                  <Table>
                    <TableHeader>
                      <TableRow className="border-border hover:bg-transparent">
                        <TableHead className="text-muted-foreground text-xs uppercase">Material</TableHead>
                        <TableHead className="text-muted-foreground text-xs uppercase">Category</TableHead>
                        <TableHead className="text-muted-foreground text-xs uppercase text-right">Quantity</TableHead>
                        <TableHead className="text-muted-foreground text-xs uppercase">Unit</TableHead>
                        <TableHead className="text-muted-foreground text-xs uppercase text-right">Loads</TableHead>
                        <TableHead className="text-muted-foreground text-xs uppercase text-right">Cost</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {data.material_breakdown.map((m, i) => (
                        <TableRow key={i} className="border-border">
                          <TableCell className="text-sm font-medium">{m.name}</TableCell>
                          <TableCell className="text-sm text-muted-foreground">{m.category}</TableCell>
                          <TableCell className="text-sm text-right font-mono">{m.quantity.toLocaleString(undefined, { minimumFractionDigits: 1 })}</TableCell>
                          <TableCell className="text-sm">{m.qty_type}</TableCell>
                          <TableCell className="text-sm text-right font-mono">{m.loads.toLocaleString()}</TableCell>
                          <TableCell className="text-sm text-right font-mono font-semibold">{m.cost > 0 ? `$${m.cost.toLocaleString(undefined, { minimumFractionDigits: 2 })}` : "—"}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </Card>
              </div>
            )}

            {/* PO Breakdown */}
            {(data.po_breakdown?.length > 0 || data.unassigned_cost > 0) && (
              <div className="mb-6">
                <h2 className="font-headline text-sm font-semibold tracking-wide uppercase text-muted-foreground mb-3">Costs by Purchase Order</h2>
                <Card className="border-border bg-card overflow-hidden">
                  <Table>
                    <TableHeader>
                      <TableRow className="border-border hover:bg-transparent">
                        <TableHead className="text-muted-foreground text-xs uppercase">PO #</TableHead>
                        <TableHead className="text-muted-foreground text-xs uppercase">Description</TableHead>
                        <TableHead className="text-muted-foreground text-xs uppercase text-right">Equip Cost</TableHead>
                        <TableHead className="text-muted-foreground text-xs uppercase text-right">Material Cost</TableHead>
                        <TableHead className="text-muted-foreground text-xs uppercase text-right">Subtotal</TableHead>
                        <TableHead className="text-muted-foreground text-xs uppercase text-right">Budget</TableHead>
                        <TableHead className="text-muted-foreground text-xs uppercase">Utilization</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {(data.po_breakdown || []).map((po, i) => (
                        <React.Fragment key={i}>
                          <TableRow className="border-border">
                            <TableCell className="font-mono text-sm font-medium">{po.po_number}</TableCell>
                            <TableCell className="text-sm text-muted-foreground max-w-[180px] truncate">{po.description || "—"}</TableCell>
                            <TableCell className="text-sm text-right font-mono">${po.equipment_cost.toLocaleString(undefined, { minimumFractionDigits: 2 })}</TableCell>
                            <TableCell className="text-sm text-right font-mono">${po.material_cost.toLocaleString(undefined, { minimumFractionDigits: 2 })}</TableCell>
                            <TableCell className="text-sm text-right font-mono font-semibold">${po.subtotal.toLocaleString(undefined, { minimumFractionDigits: 2 })}</TableCell>
                            <TableCell className="text-sm text-right font-mono">${po.budget?.toLocaleString() || "—"}</TableCell>
                            <TableCell>
                              <div className="flex items-center gap-2">
                                <div className="flex-1 h-2 rounded-full bg-muted overflow-hidden w-20">
                                  <div className={`h-full rounded-full ${po.utilization_pct >= 100 ? 'bg-red-500' : po.utilization_pct >= 80 ? 'bg-amber-500' : 'bg-emerald-500'}`} style={{ width: `${Math.min(po.utilization_pct, 100)}%` }} />
                                </div>
                                <span className={`text-xs font-mono ${po.utilization_pct >= 100 ? 'text-red-400' : po.utilization_pct >= 80 ? 'text-amber-400' : 'text-muted-foreground'}`}>{po.utilization_pct}%</span>
                              </div>
                            </TableCell>
                          </TableRow>
                          {/* Line items sub-rows */}
                          {(po.line_items || []).map((li, j) => (
                            <TableRow key={`${i}-${j}`} className="border-border/50 bg-muted/10">
                              <TableCell className="text-xs font-mono text-muted-foreground pl-8">{li.item_code}</TableCell>
                              <TableCell className="text-xs text-muted-foreground">{li.description}</TableCell>
                              <TableCell colSpan={2}></TableCell>
                              <TableCell className="text-xs text-right font-mono">${li.cost.toLocaleString(undefined, { minimumFractionDigits: 2 })}</TableCell>
                              <TableCell className="text-xs text-muted-foreground text-right">{li.lines} lines</TableCell>
                              <TableCell></TableCell>
                            </TableRow>
                          ))}
                        </React.Fragment>
                      ))}
                      {data.unassigned_cost > 0 && (
                        <TableRow className="border-border bg-muted/30">
                          <TableCell className="font-mono text-sm text-muted-foreground italic">Unassigned</TableCell>
                          <TableCell className="text-sm text-muted-foreground italic">Not covered by any PO</TableCell>
                          <TableCell colSpan={2}></TableCell>
                          <TableCell className="text-sm text-right font-mono text-muted-foreground">${data.unassigned_cost.toLocaleString(undefined, { minimumFractionDigits: 2 })}</TableCell>
                          <TableCell colSpan={2}></TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </Card>
              </div>
            )}

            {/* Contractor Breakdown */}
            {data.contractor_breakdown?.length > 0 && (
              <div className="mb-6">
                <h2 className="font-headline text-sm font-semibold tracking-wide uppercase text-muted-foreground mb-3">Contractor Summary</h2>
                <Card className="border-border bg-card overflow-hidden">
                  <Table>
                    <TableHeader>
                      <TableRow className="border-border hover:bg-transparent">
                        <TableHead className="text-muted-foreground text-xs uppercase">Contractor</TableHead>
                        <TableHead className="text-muted-foreground text-xs uppercase text-right">Reports</TableHead>
                        <TableHead className="text-muted-foreground text-xs uppercase text-right">Op Hours</TableHead>
                        <TableHead className="text-muted-foreground text-xs uppercase text-right">Delay Hours</TableHead>
                        <TableHead className="text-muted-foreground text-xs uppercase text-right">Tons</TableHead>
                        <TableHead className="text-muted-foreground text-xs uppercase text-right">CYD</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {data.contractor_breakdown.map((c) => (
                        <TableRow key={c.id} className="border-border">
                          <TableCell className="text-sm font-medium">{c.name}</TableCell>
                          <TableCell className="text-sm text-right font-mono">{c.reports}</TableCell>
                          <TableCell className="text-sm text-right font-mono">{c.op_hours.toFixed(1)}</TableCell>
                          <TableCell className="text-sm text-right font-mono text-orange-400">{c.delay_hours.toFixed(1)}</TableCell>
                          <TableCell className="text-sm text-right font-mono">{c.tons.toLocaleString()}</TableCell>
                          <TableCell className="text-sm text-right font-mono">{c.cyd.toLocaleString()}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </Card>
              </div>
            )}

            {/* Daily Summary */}
            {data.daily_summary?.length > 0 && (
              <div className="mb-6">
                <h2 className="font-headline text-sm font-semibold tracking-wide uppercase text-muted-foreground mb-3">Daily Summary</h2>
                <Card className="border-border bg-card overflow-hidden max-h-[400px] overflow-y-auto">
                  <Table>
                    <TableHeader>
                      <TableRow className="border-border hover:bg-transparent">
                        <TableHead className="text-muted-foreground text-xs uppercase">Date</TableHead>
                        <TableHead className="text-muted-foreground text-xs uppercase text-right">Reports</TableHead>
                        <TableHead className="text-muted-foreground text-xs uppercase text-right">Op Hours</TableHead>
                        <TableHead className="text-muted-foreground text-xs uppercase text-right">Delay</TableHead>
                        <TableHead className="text-muted-foreground text-xs uppercase text-right">Tons</TableHead>
                        <TableHead className="text-muted-foreground text-xs uppercase text-right">CYD</TableHead>
                        <TableHead className="text-muted-foreground text-xs uppercase text-right">Loads</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {data.daily_summary.map((d) => (
                        <TableRow key={d.date} className="border-border">
                          <TableCell className="text-sm font-mono">{d.date}</TableCell>
                          <TableCell className="text-sm text-right font-mono">{d.reports}</TableCell>
                          <TableCell className="text-sm text-right font-mono">{d.op_hours.toFixed(1)}</TableCell>
                          <TableCell className="text-sm text-right font-mono text-orange-400">{d.delay_hours.toFixed(1)}</TableCell>
                          <TableCell className="text-sm text-right font-mono">{d.tons.toLocaleString()}</TableCell>
                          <TableCell className="text-sm text-right font-mono">{d.cyd.toLocaleString()}</TableCell>
                          <TableCell className="text-sm text-right font-mono">{d.loads.toLocaleString()}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </Card>
              </div>
            )}

            {/* Export Buttons */}
            <div className="flex gap-3 justify-end" data-testid="export-buttons">
              <Button variant="outline" onClick={() => exportFile("csv")} disabled={!!exporting} data-testid="export-csv-btn" className="border-border">
                <Download className="h-4 w-4 mr-1.5" strokeWidth={1.5} />
                {exporting === "csv" ? "Exporting..." : "Export CSV"}
              </Button>
              <Button onClick={() => exportFile("pdf")} disabled={!!exporting} data-testid="export-pdf-btn" className="font-headline font-semibold">
                <Download className="h-4 w-4 mr-1.5" strokeWidth={1.5} />
                {exporting === "pdf" ? "Exporting..." : "Export Invoice PDF"}
              </Button>
            </div>
          </>
        )}

        {data && data.report_count === 0 && (
          <Card className="border-border bg-card">
            <CardContent className="py-12 text-center">
              <FileText className="h-10 w-10 text-muted-foreground mx-auto mb-3" strokeWidth={1} />
              <p className="text-muted-foreground text-sm">No submitted reports found for this period.</p>
              <p className="text-muted-foreground text-xs mt-1">Try a different date range or filters.</p>
            </CardContent>
          </Card>
        )}
      </main>
    </div>
  );
}

function KpiCard({ icon, label, value, accent }) {
  return (
    <Card className="border-border bg-card">
      <CardContent className="py-3 px-4">
        <div className="flex items-center gap-2 text-muted-foreground mb-1">
          {icon}
          <span className="text-[10px] uppercase tracking-wide">{label}</span>
        </div>
        <p className={`kpi-value text-lg ${accent || ""}`}>{value}</p>
      </CardContent>
    </Card>
  );
}
